/**
 * Aligns the authenticated Wishlist & B2B Project Planner modal copy with
 * the guest Project List terminology. The app renders its modal dynamically,
 * so its portal is observed for newly rendered text and form fields.
 */
class AuthProjectModalCopy {
  constructor() {
    this.accountPathPattern = /\/apps\/wishlist-api\/wishlist-account/i;
    this.rootObserverMap = new WeakMap();
    this.observedRoots = new WeakSet();
    this.isProcessing = false;
    this.cartSyncInProgress = false;
    this.addToCartSuccessFlagKey = 'authProjectAddToCartSuccess';
    this.wishlistCacheKey = 'authProjectWishlistCacheV1';
    this.productImageCacheKey = 'authProjectWishlistProductImagesV1';
    this.productImageCache = this.readJsonStorage(this.productImageCacheKey, {});
    this.wishlistData = this.readJsonStorage(this.wishlistCacheKey, []);
    this.pendingProductImageRequests = new Map();
    this.latestWishlistEndpoint = null;
    this.copy = new Map([
      ['my wishlists', 'My Project Lists'],
      ['my wishlist', 'My Project List'],
      ['edit wishlists', 'Edit Project Lists'],
      ['edit wishlist', 'Edit Project'],
      ['add to wishlist', 'Add to Project Lists'],
      ['add to wishlists', 'Add to Project Lists'],
      ['add to a wishlist', 'Add to Project Lists'],
      ['create new wishlist', 'Create New Project'],
      ['create a new wishlist', 'Create New Project'],
      ['create wishlist', 'Create Project'],
      ['wishlist name', 'Project name'],
      ['enter wishlist name', 'Project name'],
      ['name your wishlist', 'Project name'],
      ['add products to your wishlist', 'Add products to your project list'],
      ['no wishlists found', 'No Project Lists found'],
      ['no wishlist found', 'No Project Lists found']
    ]);

    this.patchWishlistApiFetch();
    this.setupProjectSelectionSync();
    this.observe();
    this.setupAddToCartSync();
    this.showPendingAddToCartToast();
  }

  readJsonStorage(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (error) {
      return fallback;
    }
  }

  writeJsonStorage(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage may be unavailable; use in-memory cache only.
    }
  }

  patchWishlistApiFetch() {
    if (window.__authProjectWishlistFetchPatched) return;
    if (typeof window.fetch !== 'function') return;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      const requestUrl = this.getRequestUrlFromFetchArgs(args);

      return nativeFetch(...args).then((response) => {
        this.captureWishlistResponse(requestUrl, response);
        return response;
      });
    };

    window.__authProjectWishlistFetchPatched = true;
  }

  getRequestUrlFromFetchArgs(args) {
    const [input] = args;

    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;

    return '';
  }

  captureWishlistResponse(requestUrl, response) {
    if (!response || typeof response.clone !== 'function') return;
    if (!requestUrl || !/wishlist-api/i.test(requestUrl)) return;

    const contentType = response.headers?.get?.('content-type') || '';
    if (!/json/i.test(contentType)) return;

    response.clone().json().then((data) => {
      this.ingestWishlistPayload(requestUrl, data);
    }).catch(() => {
      // Non-JSON or streamed responses are ignored.
    });
  }

  ingestWishlistPayload(requestUrl, data) {
    if (this.isWishlistArrayPayload(data)) {
      this.latestWishlistEndpoint = requestUrl;
      this.setWishlistData(data);
      return;
    }

    if (this.isWishlistProductPayload(data)) {
      this.mergeWishlistProductRecord(data);

      if (this.latestWishlistEndpoint) {
        this.refreshWishlistDataFromEndpoint();
      }
    }
  }

  async refreshWishlistDataFromEndpoint() {
    if (!this.latestWishlistEndpoint) return;

    try {
      const response = await fetch(this.latestWishlistEndpoint, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;

      const data = await response.json();
      if (this.isWishlistArrayPayload(data)) this.setWishlistData(data);
    } catch (error) {
      // Keep best-effort local cache if refresh fails.
    }
  }

  isWishlistArrayPayload(data) {
    return Array.isArray(data)
      && data.every((item) => item && typeof item === 'object' && item.id && typeof item.name === 'string');
  }

  isWishlistProductPayload(data) {
    return Boolean(
      data
      && typeof data === 'object'
      && data.wishlist_id
      && typeof data.product_handle === 'string'
    );
  }

  setWishlistData(list) {
    this.wishlistData = list.map((wishlist) => ({
      ...wishlist,
      wishlist_product_variant: Array.isArray(wishlist.wishlist_product_variant)
        ? wishlist.wishlist_product_variant
        : []
    }));

    this.writeJsonStorage(this.wishlistCacheKey, this.wishlistData);
    this.processAll();
  }

  mergeWishlistProductRecord(productRecord) {
    const wishlistId = Number(productRecord.wishlist_id);
    if (!wishlistId) return;

    const existing = this.wishlistData.find((item) => Number(item.id) === wishlistId);
    if (!existing) return;

    const variants = Array.isArray(existing.wishlist_product_variant)
      ? [...existing.wishlist_product_variant]
      : [];

    const variantId = Number(productRecord.id);
    const existingIndex = variants.findIndex((item) => Number(item.id) === variantId);

    if (existingIndex >= 0) {
      variants[existingIndex] = { ...variants[existingIndex], ...productRecord };
    } else {
      variants.push(productRecord);
    }

    existing.wishlist_product_variant = variants;
    this.writeJsonStorage(this.wishlistCacheKey, this.wishlistData);
    this.processAll();
  }

  setupProjectSelectionSync() {
    document.addEventListener('click', (event) => {
      const path = typeof event.composedPath === 'function' && event.composedPath().length
        ? event.composedPath()
        : [event.target];

      const trigger = path.find((node) => node instanceof Element && this.isProjectSelectionAction(node));
      if (!trigger) return;

      const intent = this.getProjectSelectionIntent(trigger);
      if (!intent) return;

      const row = trigger.closest("[class*='_wishlistItem_'], [class*='_wishlistsList__item_']");
      if (!row || !this.isWishlistRowElement(row)) return;

      const wishlistId = this.extractWishlistIdFromRow(row);
      const wishlistName = this.extractWishlistNameFromRow(row);
      const productHandle = this.getCurrentProductHandle(path);
      if (!productHandle) return;

      const startedAt = Date.now();
      const maxWaitMs = 2500;

      const checkForStateTransition = () => {
        const elapsed = Date.now() - startedAt;
        if (intent === 'add' && this.rowHasRemoveState(row)) {
          this.applyOptimisticProjectPreviewAdd({ wishlistId, wishlistName, productHandle });
          return;
        }

        if (intent === 'remove' && this.rowHasAddState(row)) {
          this.applyOptimisticProjectPreviewRemove({ wishlistId, wishlistName, productHandle });
          return;
        }

        if (elapsed >= maxWaitMs) return;
        setTimeout(checkForStateTransition, 120);
      };

      setTimeout(checkForStateTransition, 120);
    }, true);
  }

  isProjectSelectionAction(element) {
    if (!(element instanceof Element)) return false;

    const container = element.closest('.ssfy, #ssfy-wishlist-account, [data-ssfy-wishlist-page]');
    if (!container) return false;

    const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!text || text.length > 80) return false;

    if (text.includes('cart')) return false;
    return text.includes('add') || text.includes('remove') || text.includes('project list') || text.includes('wishlist');
  }

  getProjectSelectionIntent(element) {
    if (!(element instanceof Element)) return null;

    const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!text) return null;

    if (text.includes('remove')) return 'remove';
    if (text.includes('add')) return 'add';
    return null;
  }

  rowHasRemoveState(row) {
    if (!(row instanceof Element)) return false;

    const controls = row.querySelectorAll('button, a, [role="button"]');
    return Array.from(controls).some((control) => {
      const text = (control.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return text.includes('remove');
    });
  }

  rowHasAddState(row) {
    if (!(row instanceof Element)) return false;

    const controls = row.querySelectorAll('button, a, [role="button"]');
    return Array.from(controls).some((control) => {
      const text = (control.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return text.includes('add');
    });
  }

  getCurrentProductHandle(path = []) {
    for (const node of path) {
      if (!(node instanceof Element)) continue;

      const fromDataset = node.getAttribute('data-product-handle');
      if (fromDataset) return fromDataset.trim();

      const href = node.getAttribute('href') || '';
      const hrefMatch = href.match(/\/products\/([^/?#]+)/i);
      if (hrefMatch?.[1]) return hrefMatch[1].trim();
    }

    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    if (pathMatch?.[1]) return pathMatch[1].trim();

    return '';
  }

  applyOptimisticProjectPreviewAdd({ wishlistId, wishlistName, productHandle }) {
    if (!productHandle) return;

    const normalizedWishlistName = String(wishlistName || '').trim();
    let wishlist = null;

    if (wishlistId) {
      wishlist = this.wishlistData.find((item) => Number(item.id) === Number(wishlistId)) || null;
    }

    if (!wishlist && normalizedWishlistName) {
      wishlist = this.wishlistData.find((item) => String(item.name || '').trim().toLowerCase() === normalizedWishlistName.toLowerCase()) || null;
    }

    if (!wishlist) {
      wishlist = {
        id: wishlistId || `optimistic-${Date.now()}`,
        name: normalizedWishlistName || 'Project List',
        wishlist_product_variant: []
      };
      this.wishlistData.push(wishlist);
    }

    if (!Array.isArray(wishlist.wishlist_product_variant)) {
      wishlist.wishlist_product_variant = [];
    }

    const existingIndex = wishlist.wishlist_product_variant.findIndex((item) => String(item?.product_handle || '') === productHandle);
    const nowIso = new Date().toISOString();

    if (existingIndex >= 0) {
      wishlist.wishlist_product_variant[existingIndex] = {
        ...wishlist.wishlist_product_variant[existingIndex],
        updated_at: nowIso
      };
    } else {
      wishlist.wishlist_product_variant.push({
        id: `optimistic-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        wishlist_id: wishlist.id,
        product_handle: productHandle,
        created_at: nowIso,
        updated_at: nowIso,
        quantity: 1
      });
    }

    this.writeJsonStorage(this.wishlistCacheKey, this.wishlistData);
    this.fetchProductImageByHandle(productHandle).finally(() => this.processAll());
  }

  applyOptimisticProjectPreviewRemove({ wishlistId, wishlistName, productHandle }) {
    if (!productHandle) return;

    const normalizedWishlistName = String(wishlistName || '').trim().toLowerCase();
    const targetWishlist = this.wishlistData.find((item) => {
      if (wishlistId && Number(item.id) === Number(wishlistId)) return true;
      if (!normalizedWishlistName) return false;
      return String(item.name || '').trim().toLowerCase() === normalizedWishlistName;
    });

    if (!targetWishlist || !Array.isArray(targetWishlist.wishlist_product_variant)) return;

    targetWishlist.wishlist_product_variant = targetWishlist.wishlist_product_variant
      .filter((item) => String(item?.product_handle || '') !== productHandle);

    this.writeJsonStorage(this.wishlistCacheKey, this.wishlistData);
    this.processAll();
  }

  setupAddToCartSync() {
    document.addEventListener('click', (event) => {
      if (!this.accountPathPattern.test(window.location.pathname)) return;

      const path = typeof event.composedPath === 'function' && event.composedPath().length
        ? event.composedPath()
        : [event.target];

      const trigger = path.find((node) => node instanceof Element && this.isAddToCartTrigger(node));
      if (!trigger) return;

      // Runs in the capture phase, so this executes before the app's own click handler opens
      // the cart drawer. Suppress it immediately so it can't flash stale/empty content.
      this.suppressCartDrawerOpening();
      this.syncCartUiAfterAdd();
    }, true);
  }

  suppressCartDrawerOpening() {
    document.documentElement.classList.add('auth-project-suppress-cart-drawer');

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function' && !cartDrawer.__authProjectOpenPatched) {
      cartDrawer.__authProjectOpenPatched = true;
      cartDrawer.open = () => {};
    }
  }

  isAddToCartTrigger(element) {
    if (!(element instanceof Element)) return false;

    const container = element.closest('.ssfy, #ssfy-wishlist-account, [data-ssfy-wishlist-page]');
    if (!container) return false;

    const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!text || text.length > 60) return false;

    // Matches both the "Add all to cart" bulk action and individual per-product
    // "Add to cart" buttons rendered in the project list.
    return text.includes('add') && text.includes('cart');
  }

  async syncCartUiAfterAdd() {
    if (this.cartSyncInProgress) return;
    this.cartSyncInProgress = true;

    const initialCart = await this.fetchCartState();
    const initialCount = Number(initialCart?.item_count || 0);
    const startedAt = Date.now();
    const maxWaitMs = 45000;
    const pollIntervalMs = 600;
    let previousCount = null;
    let stableStreak = 0;

    const finish = () => {
      this.cartSyncInProgress = false;
      this.redirectToCartWithSuccess();
    };

    const checkForUpdate = async () => {
      const cartState = await this.fetchCartState();
      const currentCount = Number(cartState?.item_count || 0);
      const timedOut = Date.now() - startedAt > maxWaitMs;
      const hasNewItems = currentCount > initialCount;

      if (hasNewItems) {
        stableStreak = currentCount === previousCount ? stableStreak + 1 : 1;
        previousCount = currentCount;

        if (stableStreak >= 2) {
          finish();
          return;
        }
      }

      if (timedOut) {
        finish();
        return;
      }

      setTimeout(checkForUpdate, pollIntervalMs);
    };

    setTimeout(checkForUpdate, 450);
  }

  async fetchCartState() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      console.warn('Unable to read cart state after Add to Cart', error);
      return null;
    }
  }

  redirectToCartWithSuccess() {
    try {
      sessionStorage.setItem(this.addToCartSuccessFlagKey, '1');
    } catch (error) {
      // sessionStorage may be unavailable (e.g. private browsing); proceed with redirect regardless
    }

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    window.location.href = cartUrl;
  }

  showPendingAddToCartToast() {
    if (!/^\/cart\/?$/.test(window.location.pathname)) return;

    let hasFlag = false;
    try {
      hasFlag = sessionStorage.getItem(this.addToCartSuccessFlagKey) === '1';
      if (hasFlag) sessionStorage.removeItem(this.addToCartSuccessFlagKey);
    } catch (error) {
      hasFlag = false;
    }

    if (!hasFlag) return;

    this.renderAddToCartToast();
  }

  renderAddToCartToast() {
    const toast = document.createElement('div');
    toast.className = 'auth-project-add-to-cart-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = 'Products added to cart!';
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));

    setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      setTimeout(() => toast.remove(), 500);
    }, 3200);
  }

  observe() {
    this.observeRoot(document);
    this.processAll();
  }

  observeRoot(root) {
    if (this.observedRoots.has(root)) return;

    const observer = new MutationObserver(() => this.processAll());
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'aria-label', 'value', 'title']
    });

    this.observedRoots.add(root);
    this.rootObserverMap.set(root, observer);
  }

  processAll() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const roots = this.getProcessRoots();
    roots.forEach((root) => {
      this.observeRoot(root);
      this.ensureStyleOverride(root);
      this.updateRootCopy(root);
    });

    this.isProcessing = false;
  }

  getProcessRoots() {
    const roots = [document];
    document.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) roots.push(element.shadowRoot);
    });
    return roots;
  }

  updateRootCopy(root) {
    this.getTargets(root).forEach((container) => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((node) => {
        const parentTag = node.parentElement?.tagName;
        if (parentTag === 'SCRIPT' || parentTag === 'STYLE' || parentTag === 'NOSCRIPT') return;

        const source = node.nodeValue;
        const replacement = this.transformText(source);
        if (replacement !== source) node.nodeValue = replacement;
      });

      container.querySelectorAll('input, textarea, button, a').forEach((field) => {
        ['placeholder', 'aria-label', 'title'].forEach((attribute) => {
          const value = field.getAttribute(attribute);
          if (!value) return;

          const replacement = this.transformText(value);
          if (replacement !== value) field.setAttribute(attribute, replacement);
        });
      });

      this.upsertWishlistPreviewGrids(container);
    });
  }

  upsertWishlistPreviewGrids(container) {
    const rowSelector = "[class*='_wishlistItem_'], [class*='_wishlistsList__item_']";
    const rows = container.querySelectorAll(rowSelector);

    rows.forEach((row) => {
      if (!(row instanceof Element)) return;
      if (!this.isWishlistRowElement(row)) return;

      const parentRow = row.parentElement?.closest?.(rowSelector);
      if (parentRow && this.isWishlistRowElement(parentRow)) return;

      const titleNode = this.getWishlistTitleNode(row);
      if (!titleNode) return;

      const wishlist = this.resolveWishlistForRow(row);
      if (!wishlist) return;

      const topHandles = this.getTopHandlesForWishlist(wishlist);
      const signature = this.getPreviewSignature(topHandles);

      const existingGrids = Array.from(row.querySelectorAll(':scope > .auth-project-preview-grid'));
      if (existingGrids.length > 1) {
        existingGrids.slice(1).forEach((gridNode) => gridNode.remove());
      }

      const existingGrid = existingGrids[0] || null;
      if (existingGrid && existingGrid.getAttribute('data-preview-signature') === signature) {
        return;
      }

      const grid = this.buildPreviewGrid(topHandles);
      if (!grid) return;
      grid.setAttribute('data-preview-signature', signature);

      if (existingGrid) {
        existingGrid.replaceWith(grid);
        return;
      }

      row.classList.add('auth-project-preview-row');
      row.insertBefore(grid, row.firstChild);
    });
  }

  isWishlistRowElement(element) {
    if (!(element instanceof Element)) return false;

    const classTokens = this.getClassTokens(element);
    return classTokens.some((token) => token.startsWith('_wishlistItem_') && !token.startsWith('_wishlistItem__'))
      || classTokens.some((token) => token.startsWith('_wishlistsList__item_'));
  }

  getClassTokens(element) {
    if (!(element instanceof Element)) return [];

    return (element.getAttribute('class') || '')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }

  getWishlistTitleNode(row) {
    return row.querySelector("[class*='_wishlistItem__heading_']")
      || row.querySelector('h1, h2, h3, h4, h5, h6, strong, [role="heading"]');
  }

  buildPreviewGrid(topHandles) {
    if (!Array.isArray(topHandles)) return null;

    const grid = document.createElement('div');
    grid.className = 'auth-project-preview-grid';
    grid.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 4; index += 1) {
      const handle = topHandles[index] || null;
      const cell = document.createElement('div');
      cell.className = 'auth-project-preview-cell';

      if (!handle) {
        cell.classList.add('is-empty');
        cell.textContent = '+';
        grid.appendChild(cell);
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(this.productImageCache, handle)) {
        const cachedImage = this.productImageCache[handle];
        if (!cachedImage) {
          cell.classList.add('is-empty');
          cell.textContent = '+';
          grid.appendChild(cell);
          continue;
        }

        const image = document.createElement('img');
        image.className = 'auth-project-preview-image';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.alt = '';
        image.src = cachedImage;
        cell.appendChild(image);
      } else {
        cell.classList.add('is-loading');
        this.fetchProductImageByHandle(handle).then(() => this.processAll());
      }

      grid.appendChild(cell);
    }

    return grid;
  }

  getPreviewSignature(topHandles) {
    if (!Array.isArray(topHandles)) return 'empty';

    const cells = [];
    for (let index = 0; index < 4; index += 1) {
      const handle = topHandles[index] || '';
      if (!handle) {
        cells.push('empty');
        continue;
      }

      if (!Object.prototype.hasOwnProperty.call(this.productImageCache, handle)) {
        cells.push(`loading:${handle}`);
        continue;
      }

      const hasImage = Boolean(this.productImageCache[handle]);
      cells.push(`${hasImage ? 'img' : 'empty'}:${handle}`);
    }

    return cells.join('|');
  }

  resolveWishlistForRow(row) {
    const directId = this.extractWishlistIdFromRow(row);
    if (directId) {
      const byId = this.wishlistData.find((wishlist) => Number(wishlist.id) === directId);
      if (byId) return byId;
    }

    const rowName = this.extractWishlistNameFromRow(row);
    if (!rowName) return null;

    const normalizedName = rowName.trim().toLowerCase();
    return this.wishlistData.find((wishlist) => String(wishlist.name || '').trim().toLowerCase() === normalizedName) || null;
  }

  extractWishlistNameFromRow(row) {
    const titleNode = this.getWishlistTitleNode(row);
    if (!titleNode) return '';

    return (titleNode.textContent || '').replace(/\s+/g, ' ').trim();
  }

  extractWishlistIdFromRow(row) {
    const idAttributes = ['data-wishlist-id', 'data-id', 'data-wishlist'];

    for (const attribute of idAttributes) {
      const value = row.getAttribute(attribute);
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    for (const attr of row.getAttributeNames()) {
      if (!/wishlist/i.test(attr)) continue;
      const value = row.getAttribute(attr) || '';
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    const linked = row.querySelector('a[href*="wishlist"], button[data-wishlist-id], [data-wishlist-id]');
    if (!linked) return null;

    const linkedId = Number.parseInt(linked.getAttribute('data-wishlist-id'), 10);
    if (Number.isFinite(linkedId) && linkedId > 0) return linkedId;

    const href = linked.getAttribute('href') || '';
    const idMatch = href.match(/wishlist(?:_id|Id|\/)(\d+)/i);
    if (idMatch) {
      const parsed = Number.parseInt(idMatch[1], 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    return null;
  }

  getTopHandlesForWishlist(wishlist) {
    const variants = Array.isArray(wishlist.wishlist_product_variant)
      ? [...wishlist.wishlist_product_variant]
      : [];

    const sorted = variants.sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });

    const uniqueHandles = [];
    sorted.forEach((variant) => {
      const handle = String(variant?.product_handle || '').trim();
      if (!handle || uniqueHandles.includes(handle)) return;
      uniqueHandles.push(handle);
    });

    return uniqueHandles.slice(0, 4);
  }

  async fetchProductImageByHandle(handle) {
    if (!handle) return null;

    if (this.productImageCache[handle]) return this.productImageCache[handle];
    if (this.pendingProductImageRequests.has(handle)) return this.pendingProductImageRequests.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    }).then(async (response) => {
      if (!response.ok) return null;

      const product = await response.json();
      const image = product?.featured_image
        || (Array.isArray(product?.images) && product.images.length ? product.images[0] : null)
        || null;

      this.productImageCache[handle] = image;
      this.writeJsonStorage(this.productImageCacheKey, this.productImageCache);

      return image;
    }).catch(() => {
      this.productImageCache[handle] = null;
      this.writeJsonStorage(this.productImageCacheKey, this.productImageCache);
      return null;
    }).finally(() => {
      this.pendingProductImageRequests.delete(handle);
    });

    this.pendingProductImageRequests.set(handle, request);
    return request;
  }

  getTargets(root) {
    const modalTargets = Array.from(root.querySelectorAll('.ssfy, #ssfy-wishlist-account, [data-ssfy-wishlist-page]'));
    if (modalTargets.length) return modalTargets;

    if (root === document && this.accountPathPattern.test(window.location.pathname)) {
      const accountRoot = document.querySelector("[class*='_wishlistPageContainer_']")
        || document.querySelector("[class*='_wishlistWrapper_']")
        || document.querySelector("[class*='_wishlist__inner_']")
        || document.body;
      return accountRoot ? [accountRoot] : [];
    }

    return [];
  }

  ensureStyleOverride(root) {
    const existing = root.querySelector('style[data-auth-project-ui-overrides]');
    if (existing) return;

    const style = document.createElement('style');
    style.setAttribute('data-auth-project-ui-overrides', 'true');
    style.textContent = `

      .ssfy._modal_1qaat_1 [class*='_wishlistItem_'] {
        border-bottom: 0 !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader__wishlistsGrid_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader__wishlistsList_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__itemsWrapper_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__itemList_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader__groups_'] {
        background: #ececec !important;
        border-radius: 12px !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader__actions_'] {
        background: transparent !important;
        border-bottom: 0 !important;
        box-shadow: none !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistHeader_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] * {
        border-bottom: 0 !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader__wishlistsList_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__itemsWrapper_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__itemList_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader__groups_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] {
        border: 0 !important;
        padding: 0.8rem !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader__wishlistsList_'] [class*='_slider_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsHeader__wishlistsList_'] [class*='_slider_'] * {
        background: transparent !important;
        border-bottom: 0 !important;
        box-shadow: none !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_'] {
        background: #ffffff !important;
        border: 0 !important;
        border-radius: 12px !important;
        background-clip: padding-box;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.22), 0 1px 2px rgba(0, 0, 0, 0.08) !important;
        box-sizing: border-box;
        overflow: hidden !important;
        position: relative;
        transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_']::before,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_']::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item_']::before,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item_']::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_']::before,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_']::after {
        border: 0 !important;
        border-bottom: 0 !important;
        box-shadow: none !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'].active::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-current='true']::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-selected='true']::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item--active_']::after,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_'][aria-expanded='true']::after {
        content: none !important;
        display: none !important;
        height: 0 !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'] *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item_'] *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_'] * {
        border-bottom: 0 !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_'],
      .ssfy:not(._modal_1qaat_1) [class*='_groupsHeader__groups_'] > * {
        background: transparent !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item--active_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'].active,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-current='true'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-selected='true'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__button_'][aria-expanded='true'] {
        background: #cccccc !important;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.36), 0 2px 4px rgba(0, 0, 0, 0.12) !important;
        color: #fff !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item--active_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsDropdown__item--active_'] *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'].active,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'].active *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-current='true'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-current='true'] *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-selected='true'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistsList__item_'][aria-selected='true'] * {
        color: #fff !important;
        text-decoration: none !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProducts_'] {
        display: grid;
        gap: 1.2rem;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] {
        background: #f3f3f3 !important;
        border: 1px solid rgba(0, 0, 0, 0.12) !important;
        border-radius: 12px !important;
        box-shadow: none !important;
        margin: 0 !important;
        overflow: visible !important;
        padding: 1.6rem !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] *,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_']::before,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_']::after {
        border-bottom: 0 !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_actions_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_footer_'] {
        overflow: visible !important;
        padding-bottom: 2px !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--outline_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--text_'] {
        background: transparent !important;
        border: 1px solid rgb(var(--color-button)) !important;
        border-radius: 12px !important;
        box-sizing: border-box !important;
        color: rgb(var(--color-button)) !important;
        line-height: 1.2 !important;
        min-height: 4.2rem !important;
        overflow: visible !important;
        padding: 0.8rem 1.2rem !important;
        text-decoration: none !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--outline_']:hover,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--outline_']:focus-visible,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--text_']:hover,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--text_']:focus-visible {
        background: rgba(var(--color-button), 0.08) !important;
        border-color: rgb(var(--color-button)) !important;
        color: rgb(var(--color-button)) !important;
        text-decoration: none !important;
      }

      /* Re-enable bottom border for app default CTA buttons that get clipped by
         broad border-bottom reset rules on wishlist top/product descendants. */
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] [class*='_button--DEFAULT_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] [class*='_shareButton_'] [class*='_button--DEFAULT_'],
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--DEFAULT_'] {
        border: 1px solid rgb(var(--button-background)) !important;
        border-bottom-width: 1px !important;
        border-bottom-style: solid !important;
        box-sizing: border-box !important;
        overflow: visible !important;
      }

      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] [class*='_button--DEFAULT_']:hover,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistTop_'] [class*='_button--DEFAULT_']:focus-visible,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--DEFAULT_']:hover,
      .ssfy:not(._modal_1qaat_1) [class*='_wishlistProduct_'] [class*='_button--DEFAULT_']:focus-visible {
        border-bottom-width: 1px !important;
        border-bottom-style: solid !important;
      }

      .auth-project-add-to-cart-toast {
        position: fixed;
        bottom: 2rem;
        left: 50%;
        transform: translate(-50%, 1rem);
        background: #1a1a1a;
        color: #fff;
        padding: 1rem 1.6rem;
        border-radius: 8px;
        font-size: 1.4rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        opacity: 0;
        z-index: 9999;
        transition: opacity 0.3s ease, transform 0.3s ease;
        pointer-events: none;
      }

      .auth-project-add-to-cart-toast.is-visible {
        opacity: 1;
        transform: translate(-50%, 0);
      }

      .auth-project-suppress-cart-drawer cart-drawer {
        display: none !important;
        visibility: hidden !important;
      }

      .auth-project-suppress-cart-drawer #CartDrawer-Overlay {
        display: none !important;
      }

      .ssfy [class*='_wishlistItem_'].auth-project-preview-row,
      .ssfy [class*='_wishlistsList__item_'].auth-project-preview-row {
        align-items: center;
        display: flex;
        gap: 1rem;
      }

      .ssfy .auth-project-preview-grid {
        display: grid;
        flex: 0 0 9.2rem;
        gap: 2px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: repeat(2, minmax(0, 1fr));
        height: 9.2rem;
        width: 9.2rem;
      }

      .ssfy .auth-project-preview-cell {
        align-items: center;
        background: #e5e5e5;
        border-radius: 4px;
        display: flex;
        height: 4.4rem;
        justify-content: center;
        overflow: hidden;
        width: 4.4rem;
      }

      .ssfy .auth-project-preview-cell.is-empty,
      .ssfy .auth-project-preview-cell.is-loading {
        color: rgba(0, 0, 0, 0.35);
        font-size: 1.3rem;
        font-weight: 600;
        line-height: 1;
      }

      .ssfy .auth-project-preview-cell.is-loading::after {
        content: '...';
        letter-spacing: 0.1em;
      }

      .ssfy .auth-project-preview-cell.is-loading {
        font-size: 0;
      }

      .ssfy .auth-project-preview-image {
        display: block;
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
    `;

    if (root === document && document.head) {
      document.head.appendChild(style);
      return;
    }

    root.appendChild(style);
  }

  transformText(source) {
    const trimmed = source.trim();
    const directReplacement = this.copy.get(trimmed.toLowerCase());
    if (directReplacement) return source.replace(trimmed, directReplacement);

    return source
      .replace(/create\s+(?:a\s+)?new\s+wishlists?/gi, 'Create New Project')
      .replace(/my\s+wishlists?/gi, 'My Project Lists')
      .replace(/edit\s+wishlists?/gi, 'Edit Project')
      .replace(/create\s+wishlists?/gi, 'Create Project')
      .replace(/add\s+to\s+(?:a\s+)?wishlists?/gi, 'Add to Project Lists')
      .replace(/no\s+wishlists?\s+found/gi, 'No Project Lists found')
      .replace(/wishlist\s+name/gi, 'Project name')
      .replace(/wishlists/gi, 'Project Lists')
      .replace(/wishlist/gi, 'Project List');
  }
}

window.authProjectModalCopy = new AuthProjectModalCopy();
